import { title } from 'process';
// Copyright 2023, Avation Medical. All rights reserved.
// 
// This code is proprietary and confidential information of Avation Medical. Any use, reproduction, modification
// or distribution of the code without the express prior written consent of Avation Medical is strictly prohibited.

import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AccountService } from 'src/app/core/http/account/account.service';
import { UserService } from 'src/app/core/http/user/user.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { IconService } from 'src/app/core/services/icon.service';
import { LocationService } from 'src/app/core/services/location.service';
import { ProductSettingService } from 'src/app/core/services/product-setting.service';
import { UserSettingsService } from 'src/app/core/services/user-settings.service';
import { Navigation } from 'src/app/shared/enums/navigation';
import { handleBadRequest } from 'src/app/shared/helpers/bad-request-handler';
import { APIError } from 'src/app/shared/models/api-error.interface';
import { AccountLoginModel, ForgotPasswordModel } from 'src/app/shared/models/login';
import { emailValidator } from 'src/app/shared/validators';
import { MessageBoxComponent } from 'src/app/shared/components/message-box/message-box.component';
import { MatDialog } from '@angular/material/dialog';


export enum LoginMode {
  Existing,
  New,
  Forgot,
  Welcome
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForms: Record<LoginMode, FormGroup> = {
    [LoginMode.Existing]: new FormGroup({
      username: new FormControl('', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      // rememberPassword: new FormControl(false)
    }),
    [LoginMode.New]: new FormGroup({
      username: new FormControl('', [Validators.required])
    }),
    [LoginMode.Forgot]: new FormGroup({
      username: new FormControl('', [Validators.required])
    }),
    [LoginMode.Welcome]: new FormGroup({
      username: new FormControl('', [Validators.required, emailValidator()])
    })
  }
  title:string=null
  buttonText: string = null;
  errorStr: string = null;
  waitStr: string = null;
  hidePassword: boolean = true;
  loading: boolean = false;
  accessCodeLocked: boolean = false;
  loggedInEmail: string | null = localStorage.getItem('userEmail') || null;
  loggedInPhoneNumber: string | null = localStorage.getItem('userPhoneNumber') || null;
  accessCodeMsg: string;
  public loginMode: LoginMode = null;
  public modes = LoginMode;

  constructor(
    private readonly authService: AuthService,
    private readonly accountService: AccountService,
    private readonly settingsService: UserSettingsService,
    private readonly productSettingService: ProductSettingService,
    private readonly router: Router,
    private readonly locationService: LocationService,
    private readonly userService:UserService,
    private readonly iconService: IconService,
    private dialog: MatDialog,
  ) {
    this.iconService.registerIcons();
   }

  async ngOnInit(): Promise<void> {
    let mode=localStorage.getItem('loginMode')
    let email=localStorage.getItem('email')
    this.setTitle('Welcome')
    this.locationService.setLocation(Navigation.Login);
    this.authService.logout();
    this.productSettingService.clearItems();
    if(mode=='2'){
      this.changeMode(LoginMode.Forgot, email);
      localStorage.removeItem('loginMode')
    }else{
      this.changeMode(LoginMode.Welcome);
    }
    this.authService.authCodeLock$.subscribe(lock => {
      this.accessCodeLocked = lock;
      this.waitStr = lock ? 'Please wait for a few seconds before requesting for a new code' : null;
    });
  }

  async submitForm(): Promise<void> {
    this.loading = true;
    let form = this.loginForms[this.loginMode];
    let loginFn = this.loginMode == LoginMode.Existing ? this.login(form) :this.loginMode == LoginMode.Welcome? this.verifyUser(form):this.sendAccessCode(form, this.loginMode == LoginMode.New);

    await loginFn.catch(response => {
      this.loading = false;
      if (response.error) {
        let apiError: APIError = response.error;
        this.errorStr = apiError[""][0];
      }
      handleBadRequest(form, response);
    }).finally(() => this.loading = false);
  }

  login(form: FormGroup): Promise<any> {
    let data = form.getRawValue() as AccountLoginModel;
    return this.authService.login(data)
      .then(_ => this.settingsService.clearSessionStorage());
  }

  sendAccessCode(form: FormGroup, newAccount: boolean): Promise<any> {
    let data = { ...form.getRawValue(), newAccount } as ForgotPasswordModel;
    return this.accountService.sendForgotPassword(data).toPromise()
      .then(_ => this.router.navigate(['login/confirm-access', data.username]))
      .then(_ => this.authService.lockAccessCode());
  }

  changeMode(mode: LoginMode,email?): void {
    if (this.loginMode != mode)
    {
      this.loginForms[mode].reset();
      this.loginMode = mode;
      this.buttonText = mode == LoginMode.Existing ? 'Sign in' :mode==LoginMode.Welcome?'Continue':'Send Access Code';
      let title=mode==LoginMode.Welcome?'Welcome':mode==LoginMode.Existing?'Sign in': mode==LoginMode.New?'Sign up': 'Forgot Password'
      this.setTitle(title)
      this.errorStr=null
    }
    if (email) {
      const usernameControl = this.loginForms[mode].get('username');
      usernameControl.patchValue(email);
      if (mode == LoginMode.Existing) {
      usernameControl.disable();
      } else {
      usernameControl.enable();
      }
    }
  }

  enableSubmit(mode: LoginMode): boolean {
    if(this.loginForms[mode].invalid){
      return false
    }
    if (mode != LoginMode.Existing && mode !=LoginMode.Welcome && this.accessCodeLocked) {
      return false;
    }
    return true;
  }
  verifyUser(form){
    return this.userService.verifyUser(form.value.username).toPromise().then((res:any)=>{
      localStorage.setItem('userEmail', res.email);
      localStorage.setItem('userPhoneNumber', res.phoneNumber);
      this.loggedInEmail = res.email;
      this.loggedInPhoneNumber = res.phoneNumber;
      
      this.accessCodeMsg = res.phoneNumber && res.email
        ? 'Click “Send Access Code” to receive an access code. You will need it to set your password.'
        : `You will need an access code to set your password. Tap “Send Access Code” to email a code to ${this.loggedInEmail}.`;

      if(res.status==0){
        this.errorStr="This email address is not registered. Please contact customercare@avation.com."
      }else if(res.status==1){
        this.changeMode(LoginMode.New,res.email)
      }else{
        this.changeMode(LoginMode.Existing,res.email)
      }
    })
  }
  setTitle(title){
    this.title=title
  }

  onAccessCodeChange(e){
    const selectedValue = e.value;
    if(this.loginMode == LoginMode.New){
      this.loginForms[LoginMode.New].patchValue({ username: selectedValue });
    }else{
      this.loginForms[LoginMode.Forgot].patchValue({ username: selectedValue });
    }
  }

  forgotPassword(){
    let inputtedEmail=this.loginForms[LoginMode.Existing].get('username').value
      this.changeMode(LoginMode.Forgot, inputtedEmail);
  }

  handleBack(){
    if(this.loginMode==LoginMode.Forgot){
        this.changeMode(LoginMode.Existing, this.loggedInEmail)
    }else{
      this.changeMode(LoginMode.Welcome, this.loggedInEmail)
    }
  }
// this.changeMode(this.loginMode === this.modes.Forgot ? this.modes.Existing : this.modes.Welcome)
}
